import { useLazyQuery } from '@apollo/client'
import {
  Button,
  CircularProgress,
  Grid,
  Paper,
  TextField,
  Typography
} from '@material-ui/core'
import Alert from '@material-ui/lab/Alert'
import React, { useState } from 'react'

import Breadcrumb from '../../components/Breadcrumb'
import PageHeader from '../../components/PageHeader'
import {
  PROMOTION_EVALUATION_TRACE_QUERY,
  PromotionEvaluationTrace
} from '../../graphql/promotions'
import { getTranslate, lang } from '../../localization'
import useStyles from './styles/index.styles'

const formatTraceDate = (value: string) =>
  new Intl.DateTimeFormat(lang === 'fa' ? 'fa-IR-u-ca-persian' : 'en-GB', {
    dateStyle: 'medium',
    timeStyle: 'short'
  }).format(new Date(value))

const PromotionEvaluationTraceScreen: React.FC = () => {
  const classes = useStyles()
  const [cartId, setCartId] = useState('')
  const [bookingId, setBookingId] = useState('')
  const [inputError, setInputError] = useState('')
  const [runTrace, { data, loading, error }] = useLazyQuery<{
    promotionEvaluationTrace: PromotionEvaluationTrace
  }>(PROMOTION_EVALUATION_TRACE_QUERY, { fetchPolicy: 'network-only' })

  const submit = (event: React.FormEvent) => {
    event.preventDefault()
    const hasCartId = Boolean(cartId.trim())
    const hasBookingId = Boolean(bookingId.trim())
    if (hasCartId === hasBookingId) {
      setInputError(getTranslate('Enter exactly one cart or booking ID.'))
      return
    }
    setInputError('')
    runTrace({
      variables: {
        cartId: cartId.trim() || undefined,
        bookingId: bookingId.trim() || undefined
      }
    })
  }

  const trace = data?.promotionEvaluationTrace
  return (
    <>
      <PageHeader title={getTranslate('Promotion evaluation trace')} />
      {/*
        The section's own back-affordance, and the same component PromotionBuilder renders
        for its drill-down (PromotionBuilder/index.tsx). The trace lives INSIDE the
        promotions branch rather than beside it — the route table already says so, since
        `/promotions/evaluation-trace` only resolves by being declared above
        `/promotions/:id` — so a breadcrumb states the real relationship where a tab strip
        asserted a false one.

        `loading={false}`: both labels are static. Nothing is fetched to render them, so
        there is no skeleton state to represent.
      */}
      <Breadcrumb
        loading={false}
        levels={[
          { label: 'Promotions', link: '/promotions/list' },
          { label: 'Evaluation trace' }
        ]}
      />
      <div className={classes.detailHeader}>
        <Typography variant="h3">
          {getTranslate('Promotion evaluation trace')}
        </Typography>
      </div>
      <Paper className={classes.paper}>
        {/*
          id + aria-describedby on both fields: the message names a relationship between
          them ("exactly one"), so it belongs to both rather than to either. Marking them
          `error` too means the invalid state is visible as well as announced — previously
          the banner appeared and both inputs stayed visually and programmatically neutral,
          so nothing said which field to change.
        */}
        {inputError && (
          <Alert severity="error" id="trace-form-error" role="alert">
            {inputError}
          </Alert>
        )}
        <Typography variant="caption">
          {getTranslate('Enter either a Cart ID or a Booking ID.')}
        </Typography>
        <form onSubmit={submit}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={5}>
              <TextField
                error={Boolean(inputError)}
                aria-describedby="trace-form-error"
                fullWidth
                label={getTranslate('Cart ID')}
                value={cartId}
                onChange={(event) => {
                  setCartId(event.target.value)
                  setInputError('')
                }}
              />
            </Grid>
            <Grid item xs={12} sm={5}>
              <TextField
                error={Boolean(inputError)}
                aria-describedby="trace-form-error"
                fullWidth
                label={getTranslate('Booking ID')}
                value={bookingId}
                onChange={(event) => {
                  setBookingId(event.target.value)
                  setInputError('')
                }}
              />
            </Grid>
            <Grid item xs={12} sm={2}>
              <Button
                fullWidth
                type="submit"
                variant="contained"
                color="primary"
                disabled={loading || (!cartId.trim() && !bookingId.trim())}>
                {getTranslate('Trace')}
              </Button>
            </Grid>
          </Grid>
        </form>
        {/*
          ONE live region for the whole trace lifecycle, announcing the state as TEXT.
          Pressing Trace previously produced silence for a screen-reader user: the
          spinner has an accessible name but no live semantics, and the results render
          in a SIBLING Paper below this one, so neither transition was announced
          (WCAG 4.1.3 Status Messages).

          A single region rather than one on the spinner and another on the results —
          two regions announcing in the same moment race each other, and the second
          often wins silently. Visually hidden because the spinner and the results are
          already the visual feedback; this exists only for assistive tech.
        */}
        <div
          role="status"
          aria-live="polite"
          className={classes.visuallyHidden}>
          {loading
            ? getTranslate('Loading trace')
            : trace
            ? getTranslate('Trace ready')
            : ''}
        </div>
        {loading && <CircularProgress aria-label={getTranslate('Loading')} />}
        {error && (
          <Alert severity="error">
            {getTranslate('Unable to load trace.')}
          </Alert>
        )}
      </Paper>
      {trace && (
        <Paper className={classes.paper}>
          <Typography variant="subtitle1">
            {getTranslate('Evaluated at')}: {formatTraceDate(trace.evaluatedAt)}{' '}
            · {getTranslate('Consumed via')}: {getTranslate(trace.consumedVia)}
          </Typography>
          <Typography variant="h5" component="h4">
            {getTranslate('Applied')}
          </Typography>
          {trace.applied.length === 0 ? (
            <Typography>{getTranslate('No trace results')}</Typography>
          ) : (
            trace.applied.map((item) => (
              <Alert severity="success" key={item.promotionId}>
                {/*
                  "(minor units)" because the field is discountMinor and the app displays
                  money raw — an operator reconciling a trace against a booking total was
                  otherwise out by the currency's exponent, with a currency code beside the
                  number implying it was already converted.

                  priority and stackable are rendered because the query already fetches
                  them and this screen exists to explain WHY a promotion won. Withholding
                  the two fields that decide it made this a result list rather than an
                  explanation.
                */}
                <bdi>{item.name}</bdi> · {item.discountMinor} {item.currency} (
                {getTranslate('minor units')}) ·{' '}
                {getTranslate(item.discountType)} · {getTranslate('Priority')}:{' '}
                {item.priority} ·{' '}
                {item.stackable
                  ? getTranslate('Stackable')
                  : getTranslate('Not stackable')}
              </Alert>
            ))
          )}
          <Typography variant="h5" component="h4">
            {getTranslate('Skipped')}
          </Typography>
          {trace.skipped.length === 0 ? (
            <Typography>{getTranslate('No trace results')}</Typography>
          ) : (
            trace.skipped.map((item) => (
              <Alert severity="info" key={item.promotionId}>
                {item.name}: {item.reasonText || getTranslate(item.reason)}
              </Alert>
            ))
          )}
        </Paper>
      )}
    </>
  )
}

export default PromotionEvaluationTraceScreen
